import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { conditionalRender } from '@repo/toolkit/conditional-render'
import Button from '@repo/ui/button'
import InnerBox from '@repo/ui/inner-box'
import RadioInput from '@repo/ui/radio-input'
import SelectInput from '@repo/ui/select-input'
import Typography from '@repo/ui/typography'
import { useEffect } from 'react'

import { EnrollmentPropositionTypeEnum } from '../../../../lib/types/enums/enrollment/enrollment-proposition-type.enum'
import {
  OtoIndividualTransferMethodEnum,
  otoIndividualTransferMethodEnumMapping
} from '../../../../lib/types/enums/enrollment/oto-individual-transfer-method.enum'
import { EnrollmentProperties } from '../../../../lib/types/models/enrollment/enrollment.properties'
import { ProductOtoIndividualJourneyPlace } from '../../../../lib/types/models/product/product-oto-individual'
import { getTranslatedName } from '../../../../lib/utils/functions/translation.functions'

export type OtoIndividualDetailsValues = {
  withOption: boolean
  transferMethod?: OtoIndividualTransferMethodEnum
}

type Props = {
  regionOfStay: ProductOtoIndividualJourneyPlace
  values: OtoIndividualDetailsValues
  // the age limits are resolved against the age of the child on the departure date of the stay
  childBirthDate?: string
  departureDate?: string
  onChange: (values: OtoIndividualDetailsValues) => void
  onCancel: () => void
  onSubmit: () => void
}

const OtoIndividualPropositionDetails = ({
  regionOfStay,
  values,
  childBirthDate,
  departureDate,
  onChange,
  onCancel,
  onSubmit
}: Props) => {
  const { place, option } = regionOfStay
  const transferPrice = place.price ?? 0

  // the same rule the backend prices with: the option is imposed by the région de séjour, the
  // accueil-transfert by its lieu d'arrivée
  const childAge =
    EnrollmentProperties.getAgeAtDeparture({
      childBirthDate,
      propositionType: EnrollmentPropositionTypeEnum.OTO_INDIVIDUAL,
      otoIndividualProposition: departureDate ? { departureDate } : undefined
    }) ?? undefined
  const isOptionMandatory =
    childAge !== undefined &&
    !!regionOfStay.hasAgeLimit &&
    regionOfStay.mandatoryUnderAge != null &&
    childAge <= regionOfStay.mandatoryUnderAge
  const isTransferMandatory =
    childAge !== undefined &&
    !!place.hasAgeLimit &&
    place.mandatoryUnderAge != null &&
    childAge <= place.mandatoryUnderAge

  // a mandatory part is never a choice: the selection is forced back before it can be submitted
  useEffect(() => {
    const withOption = isOptionMandatory ? true : values.withOption
    const transferMethod = isTransferMandatory
      ? OtoIndividualTransferMethodEnum.AIRPORT_PICKUP
      : values.transferMethod
    if (withOption === values.withOption && transferMethod === values.transferMethod) return
    onChange({ ...values, withOption, transferMethod })
  }, [isOptionMandatory, isTransferMandatory, values.withOption, values.transferMethod])

  return (
    <div className="flex flex-col gap-4 mt-6">
      <InnerBox className="p-4 rounded flex flex-col gap-2 bg-white-hover">
        <Typography variant="tag" color={tailwindColors.black.DEFAULT}>
          Dans le cadre du voyage individuel, l’organisation, la réservation et le paiement du
          transport sont à la charge du participant. Ce voyage individuel s’effectuera donc sous sa
          responsabilité ou celle de ses parents.
        </Typography>
        <ul className="list-disc pl-5">
          <li>
            <Typography variant="secondary-body" color={tailwindColors.grey.DEFAULT}>
              L’arrivée à l’étranger doit impérativement avoir lieu un dimanche entre 10h00 et
              18h00, heure locale.
            </Typography>
          </li>
          <li>
            <Typography variant="secondary-body" color={tailwindColors.grey.DEFAULT}>
              Le départ de l’étranger doit impérativement avoir lieu un samedi entre 10h00 et
              18h00, heure locale.
            </Typography>
          </li>
        </ul>
        <InnerBox className="p-3 rounded bg-primary-light border-primary-light-active">
          <Typography variant="label" color={tailwindColors.black.DEFAULT}>
            <span className="font-semibold text-primary">Important</span> : avant de faire vos
            réservations, vous devez impérativement attendre que LEC vous envoie, par email, une «
            Facture – Confirmation d’inscription » sur laquelle seront mentionnées les dates de
            séjour, la région de séjour et le lieu d’arrivée que vous avez choisis.
          </Typography>
        </InnerBox>
      </InnerBox>

      <Typography variant="secondary-body" color={tailwindColors.black.DEFAULT}>
        Choix du mode d’arrivée et de départ dans la famille :
      </Typography>

      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          <RadioInput
            checked={values.transferMethod === OtoIndividualTransferMethodEnum.AIRPORT_PICKUP}
            onCheck={() =>
              onChange({
                ...values,
                transferMethod: OtoIndividualTransferMethodEnum.AIRPORT_PICKUP
              })
            }
            labelClassName="font-bold"
            label={`${
              otoIndividualTransferMethodEnumMapping[
                OtoIndividualTransferMethodEnum.AIRPORT_PICKUP
              ]
            } : ${transferPrice}€${
              place.hasAgeLimit && place.mandatoryUnderAge != null
                ? ` (cette option est obligatoire en dessous de ${place.mandatoryUnderAge} ans)`
                : ''
            }`}
          />
          <Typography variant="label" color={tailwindColors.grey.DEFAULT} className="pl-6">
            Accueil du participant à l’arrivée dans le pays de séjour et transfert jusqu’à sa
            famille et même service au retour.
            {place.hasAgeLimit && place.mandatoryUnderAge != null
              ? ` La souscription à l’accueil transfert est obligatoire pour les jeunes de moins de ${place.mandatoryUnderAge} ans le jour du départ.`
              : ''}
          </Typography>
        </div>

        <div className="flex flex-col gap-1">
          <RadioInput
            checked={values.transferMethod === OtoIndividualTransferMethodEnum.ALONE}
            disabled={isTransferMandatory}
            onCheck={() => {
              if (isTransferMandatory) return
              onChange({ ...values, transferMethod: OtoIndividualTransferMethodEnum.ALONE })
            }}
            labelClassName="font-bold"
            label={otoIndividualTransferMethodEnumMapping[OtoIndividualTransferMethodEnum.ALONE]}
          />
          <Typography variant="label" color={tailwindColors.grey.DEFAULT} className="pl-6">
            Attention : le participant devra se rendre par ses propres moyens du lieu d’arrivée
            dans le pays de séjour jusqu’à sa famille et même principe au retour. La famille
            hôtesse se situera dans un rayon d’environ 90km du lieu d’arrivée que vous avez choisi.
          </Typography>
        </div>
      </div>

      <SelectInput<number>
        label={'Option proposée pour ce séjour'}
        optional={!regionOfStay.hasAgeLimit}
        disabled={isOptionMandatory}
        options={[
          {
            label: `${getTranslatedName(option)} - ${regionOfStay.optionPrice}€`,
            value: option.id
          }
        ]}
        selectedOption={conditionalRender(values.withOption, {
          label: `${getTranslatedName(option)} - ${regionOfStay.optionPrice}€`,
          value: option.id
        })}
        onChange={(optionId) => {
          if (isOptionMandatory) return
          onChange({ ...values, withOption: optionId != null })
        }}
      />
      {regionOfStay.hasAgeLimit && regionOfStay.mandatoryUnderAge != null && (
        <Typography variant="tag" color={tailwindColors.grey.DEFAULT}>
          Cette option est obligatoire en dessous de {regionOfStay.mandatoryUnderAge} ans.
        </Typography>
      )}

      <div className="flex w-full justify-end gap-3 mb-4">
        <Button type="button" variant="secondary" onClick={onCancel} label="Précédent" />
        <Button
          type="button"
          variant="primary"
          disabled={!values.transferMethod}
          onClick={onSubmit}
          label="Sélectionner"
        />
      </div>
    </div>
  )
}

export default OtoIndividualPropositionDetails
